import { useMutation } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { toast } from "sonner";
import type { ObjPathProxy } from "ts-object-path";
import { DocumentKeys } from "web_myplann/Api/Document/DocumentKeys";
import {
	type DownloadedDocumentContent,
	downloadDocumentContent,
} from "web_myplann/Api/Document/downloadDocumentContent";
import { ApiCallError } from "web_myplann/Infrastructure/Api/ApiUtils";
import type { MyPlannResourceDictionary } from "web_myplann/Infrastructure/Translations/ResourceDictionary";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import { sessionState } from "web_myplann/Shared/State/sessionAtom";

export type DocumentVariables = { id: number; fallbackTitle?: string };

type Args<TResult> = {
	errorResource: ObjPathProxy<MyPlannResourceDictionary, string>;
	deliver: (
		document: DownloadedDocumentContent,
		errorMessage: string,
	) => Promise<TResult>;
};

export const useDocumentMutation = <TResult>({
	errorResource,
	deliver,
}: Args<TResult>) => {
	const { token, tokenVerify, authorizationType } = useAtomValue(sessionState);
	const { t } = useResource();

	return useMutation({
		mutationKey: DocumentKeys.transfer,
		mutationFn: async ({
			id,
			fallbackTitle,
		}: DocumentVariables): Promise<TResult> => {
			const errorMessage = t(errorResource);

			const document = await downloadDocumentContent({
				id,
				fallbackTitle,
				token,
				tokenVerify,
				authorizationType,
				errorMessage,
			});

			return deliver(document, errorMessage);
		},
		onError: (error) => {
			if (error instanceof ApiCallError && error.data.status === 0) {
				toast.error(t(errorResource));
			}
		},
	});
};
